import { useEffect, useState } from "react";
import { getPracticeMode, setPracticeMode, subscribePractice } from "@/lib/practice";
import { Beaker } from "lucide-react";

/**
 * Practice Mode toggle switch.
 * Gold/emerald pill that persists state across pages in the same session.
 *
 * Props:
 *  - defaultOn: seed value if sessionStorage is empty (used on /training)
 *  - className: extra wrapper classes
 *  - compact: smaller variant for dashboard headers
 */
export default function PracticeModeToggle({ defaultOn = false, className = "", compact = false }) {
  const [on, setOn] = useState(() => {
    const stored = getPracticeMode();
    // If nothing has ever been written, seed with defaultOn.
    if (typeof window !== "undefined" && sessionStorage.getItem("wa_practice_mode_v1") === null) {
      setPracticeMode(defaultOn);
      return defaultOn;
    }
    return stored;
  });

  useEffect(() => subscribePractice(setOn), []);

  const toggle = () => setPracticeMode(!on);

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="practice-mode-toggle"
      aria-pressed={on}
      className={`group inline-flex items-center gap-2 rounded-sm border transition-all ${
        compact ? "px-3 py-1.5" : "px-4 py-2"
      } ${className}`}
      style={{
        borderColor: on ? "rgba(212,175,55,0.7)" : "rgba(255,255,255,0.15)",
        background: on
          ? "linear-gradient(90deg, rgba(212,175,55,0.18), rgba(16,185,129,0.14))"
          : "rgba(6,11,20,0.5)",
        boxShadow: on ? "0 0 14px rgba(212,175,55,0.35)" : "none",
        minHeight: compact ? 34 : 40,
      }}
      title={on ? "Practice Mode is ON — no live pitches will fire" : "Practice Mode is OFF — live outreach active"}
    >
      <Beaker
        size={compact ? 12 : 14}
        style={{ color: on ? "#D4AF37" : "#64748B" }}
      />
      <span
        className={`font-mono-aviation uppercase tracking-[0.24em] ${
          compact ? "text-[9px]" : "text-[10px]"
        }`}
        style={{ color: on ? "#F8FAFC" : "#94A3B8" }}
      >
        Practice Mode
      </span>
      {/* switch track */}
      <span
        className="relative inline-block rounded-full transition-colors"
        style={{
          width: compact ? 26 : 32,
          height: compact ? 14 : 16,
          background: on ? "rgba(212,175,55,0.55)" : "rgba(255,255,255,0.15)",
        }}
        aria-hidden
      >
        <span
          className="absolute top-0.5 rounded-full transition-transform"
          style={{
            left: 2,
            width: compact ? 10 : 12,
            height: compact ? 10 : 12,
            background: on ? "#F8FAFC" : "#94A3B8",
            transform: on ? `translateX(${compact ? 12 : 16}px)` : "translateX(0)",
            boxShadow: on ? "0 0 6px rgba(212,175,55,0.7)" : "none",
          }}
        />
      </span>
    </button>
  );
}
